import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import ApiService from './ApiService';

// The FCM token this device registered with, so logout can unregister it.
const STORAGE_KEY = 'push_token';

const isNative = () => Capacitor.isNativePlatform();

const saveToken = async (token) => {
  if (!token) return;
  try {
    await ApiService.request({
      method: 'POST',
      url: 'admin/device-token',
      data: { token, platform: Capacitor.getPlatform() },
    });
    localStorage.setItem(STORAGE_KEY, token);
  } catch (error) {
    console.error('Could not register device for push:', error);
  }
};

let listenersAdded = false;

/**
 * Push notifications for the admin app (native builds only — a no-op in the
 * browser). Called once the admin is signed in (see AppShell): asks for
 * permission, registers this device's FCM token with the API so booking
 * alerts reach it, and keeps the token current when FCM rotates it.
 *
 * `onOpen(data)` runs when the admin taps a notification, with the push's data
 * payload (e.g. { type: 'booking', booking_id }).
 */
const PushService = {
  async register(onOpen) {
    if (!isNative()) return;

    try {
      let { receive } = await FirebaseMessaging.checkPermissions();
      if (receive === 'prompt' || receive === 'prompt-with-rationale') {
        ({ receive } = await FirebaseMessaging.requestPermissions());
      }
      if (receive !== 'granted') return;

      if (!listenersAdded) {
        listenersAdded = true;
        await FirebaseMessaging.addListener('tokenReceived', ({ token }) => saveToken(token));
        await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
          onOpen?.(notification?.data || {});
        });
      }

      const { token } = await FirebaseMessaging.getToken();
      await saveToken(token);
    } catch (error) {
      // Most often: Firebase isn't configured for this build yet
      // (google-services.json / GoogleService-Info.plist missing).
      console.error('Push notifications unavailable:', error);
    }
  },

  /** Stop pushes to this device — call before clearing the session on logout. */
  async unregister() {
    const token = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    if (!isNative() || !token) return;

    try {
      await ApiService.request({ method: 'POST', url: 'admin/device-token/remove', data: { token } });
    } catch (error) {
      console.error('Could not unregister device from push:', error);
    }
  },
};

export default PushService;
